/**
 * FEAT-002 — Topology editor.
 *
 * Local-state form: the live `useStationStore.evses` + `cpMaxPowerW`
 * seed an editable draft on mount. Validation issues are surfaced
 * inline; Save persists via `setTopology` (which also rebuilds the live
 * connector map). Cancel resets the draft to the live values.
 *
 * Connector ids are entered as a comma-separated list per EVSE — much
 * less ceremony than a nested table, and matches evse2's UX. The Save
 * button is disabled until the draft is dirty AND validation passes.
 */
import { useMemo, useState } from 'react';
import { connectorTypeForEvse } from '@/state/station/helpers';
import type { ConnectorId, EvseId, EVSEKind } from '@/state/types';
import type { TopologySnapshot } from '@/state/types';
import { useStationStore, type EvseState } from '@/state/useStationStore';

interface DraftEvse {
  readonly id: number;
  name: string;
  /** FEAT-011 — explicit AC/DC selection (no longer inferred from the name). */
  kind: EVSEKind;
  maxPowerW: number;
  connectorIdsText: string;
}

interface DraftTopology {
  cpMaxPowerW: number;
  evses: DraftEvse[];
}

function liveToDraft(
  evses: readonly EvseState[],
  cpMaxPowerW: number,
): DraftTopology {
  return {
    cpMaxPowerW,
    evses: evses.map((e) => ({
      id: Number(e.id),
      name: e.name,
      kind: connectorTypeForEvse(e),
      maxPowerW: e.maxPowerW,
      connectorIdsText: e.connectorIds.map((c) => Number(c)).join(','),
    })),
  };
}

function parseConnectors(text: string): readonly ConnectorId[] {
  return text
    .split(/[\s,]+/)
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n))
    .map((n) => n as ConnectorId);
}

function draftToSnapshot(draft: DraftTopology): TopologySnapshot {
  return {
    cpMaxPowerW: draft.cpMaxPowerW,
    evses: draft.evses.map((e) => ({
      id: e.id as EvseId,
      name: e.name,
      kind: e.kind,
      maxPowerW: e.maxPowerW,
      connectorIds: parseConnectors(e.connectorIdsText),
    })),
  };
}

function nextFreeEvseId(evses: readonly DraftEvse[]): number {
  return (evses.reduce((acc, e) => Math.max(acc, e.id), 0) ?? 0) + 1;
}

function nextFreeConnectorId(evses: readonly DraftEvse[]): number {
  const all = evses.flatMap((e) => parseConnectors(e.connectorIdsText).map((c) => Number(c)));
  return (all.length === 0 ? 0 : Math.max(...all)) + 1;
}

export function TopologyEditor(): JSX.Element {
  const liveEvses = useStationStore((s) => s.evses);
  const liveCpMax = useStationStore((s) => s.cpMaxPowerW);
  const setTopology = useStationStore((s) => s.setTopology);

  const [draft, setDraft] = useState<DraftTopology>(() =>
    liveToDraft(liveEvses, liveCpMax),
  );
  const [issues, setIssues] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const dirty = useMemo(() => {
    const live = JSON.stringify(liveToDraft(liveEvses, liveCpMax));
    return live !== JSON.stringify(draft);
  }, [draft, liveEvses, liveCpMax]);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const out = await setTopology(draftToSnapshot(draft));
      setIssues(out);
      if (out.length === 0) setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    setDraft(liveToDraft(liveEvses, liveCpMax));
    setIssues([]);
  };

  const handleAddEvse = (): void => {
    const id = nextFreeEvseId(draft.evses);
    const cid = nextFreeConnectorId(draft.evses);
    setDraft({
      ...draft,
      evses: [
        ...draft.evses,
        {
          id,
          name: `EVSE ${id}`,
          kind: 'AC',
          maxPowerW: 22_000,
          connectorIdsText: String(cid),
        },
      ],
    });
  };

  const handleRemoveEvse = (id: number): void => {
    setDraft({ ...draft, evses: draft.evses.filter((e) => e.id !== id) });
  };

  const updateEvse = (id: number, patch: Partial<DraftEvse>): void => {
    setDraft({
      ...draft,
      evses: draft.evses.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    });
  };

  return (
    <div className="space-y-3" data-testid="topology-editor">
      <div className="flex items-center gap-2 text-xs">
        <label className="text-slate-400" htmlFor="topology-cpMaxPowerW">
          Charge Point max power (kW)
        </label>
        <input
          id="topology-cpMaxPowerW"
          type="number"
          min={0}
          step="0.1"
          value={draft.cpMaxPowerW / 1000}
          onChange={(e) =>
            setDraft({ ...draft, cpMaxPowerW: Math.round(Number(e.target.value) * 1000) })
          }
          className="w-32 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="topology-cpMaxPowerW"
        />
        <span className="text-slate-500">overall station limit</span>
      </div>

      <ul className="space-y-2" data-testid="topology-evse-list">
        {draft.evses.map((e) => (
          <li
            key={e.id}
            className="rounded border border-slate-700 bg-slate-900/40 p-2 text-xs"
            data-testid={`topology-evse-${e.id}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-slate-500">#{e.id}</span>
              <input
                type="text"
                value={e.name}
                onChange={(ev) => updateEvse(e.id, { name: ev.target.value })}
                className="w-36 rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100"
                placeholder="EVSE name"
                aria-label={`EVSE ${e.id} name`}
                data-testid={`topology-evse-${e.id}-name`}
              />
              <select
                value={e.kind}
                onChange={(ev) =>
                  updateEvse(e.id, { kind: ev.target.value as EVSEKind })
                }
                className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100"
                aria-label={`EVSE ${e.id} current type`}
                data-testid={`topology-evse-${e.id}-kind`}
              >
                <option value="AC">AC</option>
                <option value="DC">DC</option>
              </select>
              <label className="flex items-center gap-1 text-slate-400">
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={e.maxPowerW / 1000}
                  onChange={(ev) =>
                    updateEvse(e.id, {
                      maxPowerW: Math.round(Number(ev.target.value) * 1000),
                    })
                  }
                  className="w-24 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100"
                  aria-label={`EVSE ${e.id} max power kW`}
                  data-testid={`topology-evse-${e.id}-maxPowerW`}
                />
                <span>kW</span>
              </label>
              <input
                type="text"
                value={e.connectorIdsText}
                onChange={(ev) =>
                  updateEvse(e.id, { connectorIdsText: ev.target.value })
                }
                className="w-32 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100"
                placeholder="connectors e.g. 1,2"
                aria-label={`EVSE ${e.id} connector ids`}
                data-testid={`topology-evse-${e.id}-connectors`}
              />
              <button
                type="button"
                onClick={() => handleRemoveEvse(e.id)}
                disabled={draft.evses.length <= 1}
                className="ml-auto rounded bg-rose-700 px-2 py-1 text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-40"
                title={draft.evses.length <= 1 ? 'At least one EVSE is required' : 'Remove EVSE'}
                data-testid={`topology-evse-${e.id}-remove`}
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button
          type="button"
          onClick={handleAddEvse}
          className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid="topology-add-evse"
        >
          Add EVSE
        </button>
        <button
          type="button"
          onClick={() => {
            void handleSave();
          }}
          disabled={!dirty || saving}
          className="rounded bg-primary-hover px-2 py-1 text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="topology-save"
        >
          Save topology
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={!dirty || saving}
          className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="topology-cancel"
        >
          Cancel
        </button>
      </div>

      {issues.length > 0 ? (
        <ul
          className="space-y-1 rounded border border-rose-700 bg-rose-950/30 p-2 text-[11px] text-rose-100"
          data-testid="topology-issues"
        >
          {issues.map((iss) => (
            <li key={iss}>{iss}</li>
          ))}
        </ul>
      ) : null}
      {savedAt !== null ? (
        <p className="text-[11px] text-primary-soft" data-testid="topology-saved-at">
          Saved at {new Date(savedAt).toISOString()}
        </p>
      ) : null}
    </div>
  );
}
